import { useCallback, useMemo, useRef, useSyncExternalStore } from "react";

export type HeaderScrollState = {
  isAtTop: boolean;
  isScrolled: boolean;
  isScrollingUp: boolean;
  isVisible: boolean;
  isHidden: boolean;
  isTransitionDisabled: boolean;
  isVisibleOverlay: boolean;
};

type HeaderScrollStoreConfig = {
  headerHeight: number;
  topBannersHeight: number;
  headerPortalHeight: number;
  overlayHeader: boolean;
};

type HeaderScrollSnapshot = {
  scrollY: number;
  prevScrollY: number;
};

const areScrollStatesEqual = (prev: HeaderScrollState, next: HeaderScrollState): boolean => {
  return (
    prev.isAtTop === next.isAtTop &&
    prev.isScrolled === next.isScrolled &&
    prev.isScrollingUp === next.isScrollingUp &&
    prev.isVisible === next.isVisible &&
    prev.isHidden === next.isHidden &&
    prev.isTransitionDisabled === next.isTransitionDisabled &&
    prev.isVisibleOverlay === next.isVisibleOverlay
  );
};

const areConfigsEqual = (a: HeaderScrollStoreConfig, b: HeaderScrollStoreConfig): boolean => {
  return (
    a.headerHeight === b.headerHeight &&
    a.topBannersHeight === b.topBannersHeight &&
    a.headerPortalHeight === b.headerPortalHeight &&
    a.overlayHeader === b.overlayHeader
  );
};

class HeaderScrollStore {
  private listeners = new Set<() => void>();
  private snapshot: HeaderScrollSnapshot = {
    scrollY: 0,
    prevScrollY: 0,
  };
  private cachedState: HeaderScrollState | null = null;
  private cachedConfig: HeaderScrollStoreConfig | null = null;

  constructor() {
    if (typeof window !== "undefined") {
      this.snapshot.scrollY = window.scrollY;
      this.snapshot.prevScrollY = window.scrollY;
    }
  }

  subscribe = (listener: () => void) => {
    if (this.listeners.size === 0 && typeof window !== "undefined") {
      window.addEventListener("scroll", this.handleScroll, { passive: true });
    }
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
      if (this.listeners.size === 0 && typeof window !== "undefined") {
        window.removeEventListener("scroll", this.handleScroll);
      }
    };
  };

  private handleScroll = () => {
    const currentScrollY = window.scrollY;
    const prevScrollY = this.snapshot.scrollY;

    this.snapshot = {
      scrollY: currentScrollY,
      prevScrollY,
    };
    this.notifyListeners();
  };

  private notifyListeners() {
    this.listeners.forEach((listener) => listener());
  }

  getSnapshot = (config: HeaderScrollStoreConfig): HeaderScrollState => {
    if (this.cachedState && this.cachedConfig && areConfigsEqual(this.cachedConfig, config)) {
      const candidate = this.computeState(config);
      if (areScrollStatesEqual(this.cachedState, candidate)) {
        return this.cachedState;
      }
      this.cachedState = candidate;
      this.cachedConfig = config;
      return candidate;
    }

    const state = this.computeState(config);
    this.cachedState = state;
    this.cachedConfig = config;
    return state;
  };

  private computeState(config: HeaderScrollStoreConfig): HeaderScrollState {
    const { scrollY, prevScrollY } = this.snapshot;
    const { headerHeight, topBannersHeight, headerPortalHeight, overlayHeader } = config;

    const isAtTop = scrollY <= 0;
    const isScrolled = scrollY > 0;
    const isScrollingUp = scrollY < prevScrollY || isAtTop;
    const isVisible = isScrollingUp;

    const isHidden = !isVisible && overlayHeader && headerHeight + topBannersHeight - headerPortalHeight < scrollY;
    const isTransitionDisabled = headerHeight + topBannersHeight + 1 > scrollY && overlayHeader;
    const isVisibleOverlay = isVisible && overlayHeader && scrollY >= topBannersHeight;

    return {
      isAtTop,
      isScrolled,
      isScrollingUp,
      isVisible,
      isHidden,
      isTransitionDisabled,
      isVisibleOverlay,
    };
  }

  getServerSnapshot = (): HeaderScrollState => {
    return {
      isAtTop: true,
      isScrolled: false,
      isScrollingUp: false,
      isVisible: true,
      isHidden: false,
      isTransitionDisabled: false,
      isVisibleOverlay: false,
    };
  };
}

export function useHeaderScrollStore(config: HeaderScrollStoreConfig): HeaderScrollState {
  const { headerHeight, topBannersHeight, headerPortalHeight, overlayHeader } = config;

  const storeRef = useRef<HeaderScrollStore | null>(null);
  if (!storeRef.current) {
    storeRef.current = new HeaderScrollStore();
  }
  const store = storeRef.current;

  const stableConfig = useMemo(
    () => ({ headerHeight, topBannersHeight, headerPortalHeight, overlayHeader }),
    [headerHeight, topBannersHeight, headerPortalHeight, overlayHeader]
  );

  const getSnapshot = useCallback(() => store.getSnapshot(stableConfig), [store, stableConfig]);

  return useSyncExternalStore(store.subscribe, getSnapshot, store.getServerSnapshot);
}
